<template>
  <div class="cm-horizontal-scrolling-base" :class="{ 'is-loaded': isLoaded }">
    <div ref="horizontalScrolling" class="inner-container container-full">
      <div class="hs-container">
        <!-- Head -->
        <div class="head-container">
          <slot name="head"></slot>
        </div>

        <!-- hs-wrapper -->
        <Swiper v-if="isSlider" slidesPerView="auto" class="swiper" :threshold="4" @swiper="onSwiperInit">
          <slot name="cards"></slot>
          <template #container-end>
            <div v-if="!swiperInstance?.isLocked" class="arrows">
              <slot name="arrows"></slot>
            </div>
          </template>
        </Swiper>
        <div v-else ref="hsWrapper" class="hs-wrapper grid-gutter">
          <slot name="cards"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import type { Swiper as SwiperInstance } from 'swiper/types';
import { Swiper } from 'swiper/vue';
import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useBreakpoint } from '@seb-platform/shared/composables';

gsap.registerPlugin(ScrollTrigger);

const props = defineProps<{
  progress: number;
  sizeMap: {
    square: [number, number, number];
    portrait: [number, number, number];
    landscape: [number, number, number];
  };
  isSlider?: boolean;
}>();

const { state: stateBreakpoint } = useBreakpoint();

const $horizontalScrolling = useTemplateRef('horizontalScrolling');
const $hsWrapper = useTemplateRef('hsWrapper');
const columnWidth = ref(0);
const gapWidth = ref(0);
const tweenedProgress = ref<gsap.core.Tween>();
const isLoaded = ref(false);
const isMobile = computed(() => !stateBreakpoint.isPhoneLarge);
const isLaptop = computed(() => stateBreakpoint.isLaptop);
const swiperInstance = ref<SwiperInstance | null>(null);

/**
 * Converts the number of columns to a pixel value.
 */
function getWidth(size: number) {
  // Mobile always has a width of 10 columns
  const columns = !isMobile.value || size === 1 ? size : 10;
  // Gaps between columns
  const gapsWidth = gapWidth.value * (columns - 1);
  const width = columnWidth.value * columns + gapsWidth;
  return `${width}px`;
}

/**
 * Returns the number of columns based on the format and size.
 */
function getColumnSize(
  format: '' | 'portrait' | 'square' | 'landscape' = 'square',
  isLarge: boolean,
  forceSize?: number
): number {
  if (forceSize) {
    return forceSize;
  }
  const _isTabletOnly = stateBreakpoint.isPhoneLarge && !stateBreakpoint.isTablet;
  switch (format) {
    case 'square':
      // On tablet, portrait is always large
      return isLarge ? props.sizeMap['square'][0] : props.sizeMap['square'][_isTabletOnly ? 1 : 2];
    case 'portrait':
      // On tablet, portrait is always large
      return isLarge ? props.sizeMap['portrait'][0] : props.sizeMap['portrait'][_isTabletOnly ? 1 : 2];
    case 'landscape':
      return isLarge ? props.sizeMap['landscape'][0] : props.sizeMap['landscape'][_isTabletOnly ? 1 : 2];
    default:
      return 1;
  }
}

/**
 * Sets the column width based on the container width and the number of columns.
 */
function setColumnWidth() {
  const maxWidth = 1280; // Max width 1280px to avoid y overflow
  let containerWidth = $horizontalScrolling.value?.offsetWidth ?? 1;
  gapWidth.value = isLaptop.value ? 24 : !isMobile.value ? 16 : 8; // 24px gap for laptop, 16px for tablet, 8px for mobile
  containerWidth = containerWidth > maxWidth ? maxWidth : containerWidth;
  columnWidth.value = (containerWidth - gapWidth.value * 11) / 12; // 11 gaps, 12 columns without gaps
  isLoaded.value = true;
}
const debouncedSetColumnWidth = useDebounceFn(setColumnWidth, 300);

/**
 * Helper types to make sure that the given types share the same base structure.
 */

interface CardBase {
  component: 'item_card-content-svg' | 'item_card-content-numbers' | 'item_card-content-media';
  format?: '' | 'portrait' | 'square' | 'landscape';
  _uid: string;
  [key: string]: any;
}

interface ClusterBase {
  card_1?: CardBase[];
  card_1_is_large?: boolean;
  card_2?: CardBase[];
  card_2_is_large?: boolean;
  card_3?: CardBase[];
  card_3_is_large?: boolean;
  _uid: string;
  [key: string]: any;
}

/**
 * Gets the actual with of the card in pixels.
 */
function getCardWidth(items: ClusterBase[], clusterIndex: number, itemIndex: number) {
  const cluster = items[clusterIndex];
  let size = 1;
  let isLarge = false;
  let card: CardBase | undefined;

  if (!cluster) {
    return getWidth(size);
  }

  switch (itemIndex) {
    case 0: {
      card = cluster.card_1?.[0];
      isLarge = !!cluster.card_1_is_large;
      break;
    }
    case 1: {
      card = cluster.card_2?.[0];
      isLarge = !!cluster.card_2_is_large;
      break;
    }
    case 2: {
      card = cluster.card_3?.[0];
      isLarge = !!cluster.card_3_is_large;
      break;
    }
    default:
      break;
  }

  if (!card) {
    return getWidth(size);
  }

  const isSvg = card.component === 'item_card-content-svg';
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const formatCard = !isSvg ? card.format || undefined : undefined;
  size = getColumnSize(formatCard, isLarge, isSvg ? 3 : undefined);

  return getWidth(size);
}

/**
 * Moves the cards to the correct position based on the progress.
 */
function updateCardPositions() {
  if (!$hsWrapper.value || props.isSlider) {
    return;
  }

  if (isMobile.value) {
    // Remove tween if it exists
    if (tweenedProgress.value) {
      tweenedProgress.value = gsap.to($hsWrapper.value, {
        duration: 0,
        x: 0,
      });
      tweenedProgress.value.kill();
    }
  } else {
    const maxX = $hsWrapper.value.scrollWidth - $hsWrapper.value.clientWidth + 200;
    tweenedProgress.value = gsap.to($hsWrapper.value, {
      ease: 'expo.out',
      duration: 0.5,
      x: -props.progress * maxX,
    });
  }
}

/**
 * Swiper related functions
 */
function onSwiperInit(swiper: SwiperInstance) {
  swiperInstance.value = swiper;
}

function slideNext() {
  changeSlide(1);
}

function slidePrev() {
  changeSlide(-1);
}

/**
 * We need to skip spacer slides, so we check for an `is-spacer` class on the slide before moving to it.
 */
function changeSlide(changeBy: -1 | 1) {
  if (!swiperInstance.value) {
    return;
  }
  const nextSlide = swiperInstance.value.slides[swiperInstance.value.activeIndex + changeBy];
  if (nextSlide && nextSlide.classList.contains('is-spacer')) {
    swiperInstance.value.slideTo(swiperInstance.value.activeIndex + changeBy * 2);
  } else {
    swiperInstance.value.slideTo(swiperInstance.value.activeIndex + changeBy);
  }
}

/**
 * Watchers
 */

watch(() => props.progress, updateCardPositions);

onMounted(() => {
  void nextTick(setColumnWidth);
  window.addEventListener('resize', debouncedSetColumnWidth);
});

onUnmounted(() => {
  window.removeEventListener('resize', debouncedSetColumnWidth);
  if (tweenedProgress.value) {
    tweenedProgress.value.kill();
  }
});

defineExpose({
  getWidth,
  getColumnSize,
  getCardWidth,
  swiperInstance,
  slideNext,
  slidePrev,
});
</script>

<style src="./SharedCMHorizontalScrollingBase.scss" lang="scss" scoped />
